import { syncedGet, syncedSet, SERVER_URL } from './serverStore';

const KEYS = { users: 'sl_users' };
const load = (key, fb) => syncedGet(key, fb);
const save = (key, val) => syncedSet(key, val);

let _session = null;

function hashPassword(pw) {
  let hash = 0;
  for (let i = 0; i < pw.length; i++) {
    const char = pw.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return 'h_' + Math.abs(hash).toString(36) + '_' + pw.length;
}
function generateId() { return Date.now().toString(36) + Math.random().toString(36).slice(2,6); }

export const ALL_SCREENS = [
  {id:'ops',      label:'Operations',       icon:'📊'},
  {id:'finance',  label:'Finance',          icon:'💹'},
  {id:'labboard', label:'Lab Board',        icon:'🧪'},
  {id:'loginqueue', label:'Incoming Samples', icon:'📦'},
  {id:'log',      label:'Log Report',       icon:'📋'},
  {id:'receive',  label:'Sample Receiving', icon:'📥'},
  {id:'setup',    label:'Sample Setup',     icon:'🔬'},
  {id:'results',  label:'Enter Results',    icon:'✅'},
  {id:'reports',  label:'Reports',          icon:'📤'},
  {id:'history',  label:'History',          icon:'📚'},
  {id:'my_tasks', label:'My Tasks',         icon:'☑️'},
  {id:'employee_tasks', label:'Employee Tasks', icon:'📋'},
  {id:'calcs',    label:'Calculators',      icon:'🔢'},
  {id:'hours',    label:'Hours Log',        icon:'⏱'},
  {id:'invoice',  label:'Billing',          icon:'💰'},
  {id:'quotes',   label:'Quotes',           icon:'📋'},
  {id:'email',    label:'Email Setup',      icon:'📧'},
  {id:'admin',    label:'Admin',            icon:'⚙️'},
];

export const ROLE_PRESETS = {
  admin:      { label:'Administrator',  color:'#b83232', screens: ALL_SCREENS.map(s=>s.id) },
  technician: { label:'Lab Technician', color:'#1a7dc4', screens: ['my_tasks','labboard','loginqueue','log','receive','setup','results','calcs','hours','email'] },
  viewer:     { label:'Report Viewer',  color:'#1a6b42', screens: ['my_tasks','ops','finance','labboard','log','reports','calcs','hours','quotes','email'] },
  billing:    { label:'Billing',        color:'#e09900', screens: ['my_tasks','ops','log','reports','invoice','quotes','hours','email'] },
  custom:     { label:'Custom',         color:'#9c27b0', screens: [] },
};

export const auth = {
  hashPassword,
  ALL_SCREENS,
  ROLE_PRESETS,

  // Never expose the server cache's live array to Admin forms. Mutating that
  // array before syncedSet captured its base made a new account look saved in
  // the current browser while the concurrency merge correctly retained the
  // older server list. A detached copy keeps the server base unchanged until
  // the confirmed write is submitted.
  getUsers: () => load(KEYS.users, []).map(user => ({
    ...user,
    screens: Array.isArray(user.screens) ? [...user.screens] : [],
  })),
  saveUsers: (arr) => save(KEYS.users, arr.map(user => ({
    ...user,
    screens: Array.isArray(user.screens) ? [...user.screens] : [],
  }))),
  getUsersAsync: async function() { return this.getUsers(); },

  addUser(data) {
    const users = this.getUsers();
    if (users.find(u => u.username.toLowerCase() === data.username.toLowerCase()))
      return { error: 'Username already exists' };
    const user = {
      id: generateId(),
      username: data.username.trim(),
      displayName: data.displayName || data.username,
      passwordHash: hashPassword(data.password),
      role: data.role || 'technician',
      screens: data.screens || ROLE_PRESETS[data.role]?.screens || [],
      active: true,
      createdAt: new Date().toISOString(),
      lastLogin: null,
    };
    users.push(user);
    this.saveUsers(users);
    return { user };
  },

  updateUser(id, updates) {
    const users = this.getUsers();
    const idx = users.findIndex(u => u.id === id);
    if (idx === -1) return false;
    if (updates.password) { updates.passwordHash = hashPassword(updates.password); delete updates.password; }
    users[idx] = { ...users[idx], ...updates };
    this.saveUsers(users);
    return true;
  },

  deleteUser(id) {
    this.saveUsers(this.getUsers().filter(u => u.id !== id));
    return true;
  },

  changePassword(userId, newPw) { return this.updateUser(userId, { passwordHash: hashPassword(newPw) }); },

  async getSession() {
    try {
      const res = await fetch(`${SERVER_URL}/session`, { credentials: 'include' });
      if (!res.ok) {
        _session = null;
        return null;
      }
      const data = await res.json();
      _session = data.session || null;
      return _session;
    } catch {
      _session = null;
      return null;
    }
  },

  getCachedSession: () => _session,

  async login(username, password) {
    try {
      const res = await fetch(`${SERVER_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok || data.error) return { error: data.error || 'Login failed' };
      _session = data.session;
      return { session: data.session };
    } catch {
      return { error: 'Could not reach the server. Check your connection and try again.' };
    }
  },

  async logout() {
    try {
      await fetch(`${SERVER_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Best effort: always clear the browser's in-memory session.
    }
    _session = null;
  },

  canAccess(id) {
    if (!_session) return false;
    // Administrators always retain access to newly introduced screens, even
    // when their saved account predates that screen's permission identifier.
    if (_session.role === 'admin') return true;
    return _session.screens.includes(id);
  },
  isAdmin() { return _session?.role === 'admin'; },
  async loginAsync(u,p) { return this.login(u,p); },
};

export default auth;
